import Player from 'react-player/file';

import { Rotate } from 'services/file.service';
import { AspectRatio } from 'services/clip.service';
import { getInternalPlayer } from 'components/app/_helpers/_app.helper';
import { RotateListener } from 'components/app/editor/_mediator/editor.slice';

type Position = number; // 0-2

export const rotatePos = (moveX: Position, moveY: Position, rotateData: RotateListener, isInternal?: 'internal') => {
  const { rotate, flip } = rotateData;
  if (flip) moveY = 2 - moveY;
  if (isInternal && !flip) {
    if (rotate === Rotate.d90) return { moveX: 2 - moveY, moveY: moveX };
    if (rotate === Rotate.d270) return { moveX: moveY, moveY: 2 - moveX };
  }
  if (rotate === Rotate.d180) return { moveX: 2 - moveX, moveY: 2 - moveY };
  if (rotate === Rotate.d90) return { moveX: moveY, moveY: 2 - moveX };
  if (rotate === Rotate.d270) return { moveX: 2 - moveY, moveY: moveX };
  return { moveX, moveY };
};

export const convertPosition = (
  position: number,
  currFormat: 'internal' | 'external',
  viewerSize: number,
  frameSize: number
) => {
  if (currFormat === 'external') {
    // convert backend value 0-2 into frontend value (which is depends on video size)
    const fraction = position / 2;
    const pixels = viewerSize * fraction;
    return pixels - frameSize / 2;
  }
  const pixels = position + frameSize / 2;
  const fraction = pixels / viewerSize;
  return fraction * 2;
};

export const getViewerSize = (element: Player | HTMLImageElement, rotate: Rotate) => {
  const isVertical = [Rotate.d90, Rotate.d270].includes(rotate);
  let elem = { width: 0, height: 0 };
  let frameAR = 0;

  const getElSize = (width: number, height: number, isVertical: boolean) =>
    isVertical ? { width: height, height: width } : { width, height };
  const getAR = (width: number, height: number, isVertical: boolean) => (isVertical ? height / width : width / height);

  if (element instanceof HTMLImageElement) {
    elem = getElSize(element.clientWidth, element.clientHeight, isVertical);
    frameAR = getAR(element.naturalWidth, element.naturalHeight, isVertical);
    //
  } else if (element instanceof Player) {
    const player = getInternalPlayer(element);
    if (!player) return;
    elem = getElSize(player.clientWidth, player.clientHeight, isVertical);
    frameAR = getAR(player.videoWidth, player.videoHeight, isVertical);
  }
  const playerAR = elem.width / elem.height;
  const emptySpace =
    playerAR > frameAR
      ? { height: 0, width: elem.width - elem.height * frameAR }
      : { width: 0, height: elem.height - elem.width / frameAR };

  return {
    width: elem.width - emptySpace.width,
    height: elem.height - emptySpace.height,
  };
};

export const getARViewerSize = (element: HTMLDivElement, AR: AspectRatio, rotate: Rotate | null) => {
  const { width, height } = element.getBoundingClientRect();
  const getViewerSize = (w: number, h: number) => {
    const AR = width / height;
    const isInv = [Rotate.d90, Rotate.d270].includes(rotate ?? Rotate.default);
    const _ar1 = isInv ? h / w : w / h;
    const _ar2 = isInv ? w / h : h / w;
    return AR > _ar1 ? { width: height * _ar1, height } : { width, height: width * _ar2 };
  };
  switch (AR) {
    case AspectRatio.is16x9:
      return getViewerSize(16, 9);
    case AspectRatio.is9x16:
      return getViewerSize(9, 16);
    default:
      return { width, height };
  }
};
